import type { DeltaState } from '@/features/chat/stream';
import type { NavigateEntity } from '@/features/chat/entity-route';
import type { DocumentType } from '@/features/documents/constants';
import { ChatRole } from '@/features/chat/constants';
import { Citation } from '@/features/documents/types';

export enum ChatEventType {
  STATUS = 'chatstatus',
  RESPONSE = 'chatresponse',
  RESPONSE_DELTA = 'chatresponse_delta',
  TITLE = 'chattitle',
  CITATIONS = 'citations',
  DONE = 'done',
  ERROR = 'error',
  CLARIFICATION = 'clarification',
  CLARIFICATION_RESPONSE = 'clarification_response',
  INVALIDATE = 'invalidate',
  NAVIGATE = 'navigate',
  STEP = 'step',
  DRAFT_OPERATIONS = 'draftoperations',
  INSUFFICIENT_BASIS = 'insufficient_basis',
  PRIOR_RUN = 'prior_run',
  THOUGHT = 'thought',
  RUN_COMPLETED = 'run_completed',
  REVISION_DELTA = 'revision_delta',
  WEB_FINDINGS = 'web_findings',
  SPECIALIST_RESULT = 'specialist_result',
  ARTIFACT = 'artifact',
  CANCELLED = 'cancelled',
}

export enum ChatActionType {
  CONFIRM_IMPLEMENT = 'confirm_implement',
  SAVE_ARTIFACT = 'agent_save_artifact',
  TENDER_ANALYSIS_SUGGESTION = 'tender_analysis_suggestion',
  BID_CHAT_ANALYSIS_SUGGESTION = 'bid_chat_analysis_suggestion',
  CONTRACT_ANALYSIS_SUGGESTION = 'contract_analysis_suggestion',

  KG_WP_COLLECT_DISCIPLINES = 'kg_wp_collect_disciplines',
  KG_WP_RESOLVE_TEMPLATE = 'kg_wp_resolve_template',
}

export enum ConfirmOption {
  CONFIRM = 'confirm',
}

export type ChatStartTurnResponse = {
  messageId: string;
};

export type ClarificationData = {
  clarificationId: string;
  actionType: string;
  question: string;
  options: Array<{
    id: string;
    label: string;
    metadata?: { params?: ClarificationOptionParams } | null;
  }>;
  allowMultiple: boolean;
  draft?: {
    selected?: string[];
    name?: string;
    other?: string;
  };
  response?: {
    selected: string[];
    freeformInput: string | null;
    resolution?: 'answered' | 'cancelled' | 'superseded';
  };
  files?: Array<{
    name: string;
    size?: number | string;
  }>;
};

export type QueuedChatMessage = {
  id: string;
  userPrompt: string;
  specifiedDocuments?: string[];
  send: (item: QueuedChatMessage) => void;
};

export type ThinkingStepStatus =
  | 'complete'
  | 'active'
  | 'pending'
  | 'failed'
  | 'cancelled'
  | 'interrupted';

export type ActivityKind = 'search' | 'read' | 'check' | 'write' | 'delegate' | 'wait';

export type ThinkingStepData = {
  parentId?: string;
  agentId?: string;
  agentName?: string;
  operationId?: string;
  resourceId?: string;
  attempt?: number;
  kind?: ActivityKind;
  citations?: Citation[];
  reasoning?: string;
  thoughts?: ThinkingStepData[];
  artifacts?: ChatArtifact[];
  id: string;
  groupId?: string;
  startedAt?: string;
  completedAt?: string;
  label: string;
  description?: string;
  status?: ThinkingStepStatus;
  details?: string[];
  detailsSummary?: string;
};

export type InsufficientBasisReasonKind =
  | 'missing_document'
  | 'analysis_not_run'
  | 'out_of_scope'
  | 'insufficient_detail';

export type InsufficientBasisData = {
  reasonKind: InsufficientBasisReasonKind;
  missingSource?: string;
  closestMaterial?: string;
};

export type RunCompletedData = {
  runId: string;
  batchProgress?: { total: number; completed: number };
  navigateEntity?: NavigateEntity;
  navigateEntityId?: string;
  completedAt?: string;
  stale?: boolean;
};

export type RevisionDeltaItem = {
  label: string;
  detail?: string | null;
};

export type RevisionDeltaData = {
  displayName: string;
  compareRunId: string;
  baseCompletedAt?: string | null;
  compareCompletedAt?: string | null;
  improved: RevisionDeltaItem[];
  worsened: RevisionDeltaItem[];
  added: RevisionDeltaItem[];
  removed: RevisionDeltaItem[];
  countsDelta: Record<string, number>;
  method: 'llm_digest' | 'summary_stats';
};

export type WebFindingsData = {
  query: string;
  provider: string;
  results: Array<{ title: string; url: string; snippet: string }>;
};

export type ClarificationOptionParams = {
  language?: string;
  docIds?: string[];
  templateId?: string;
};

export type ChatFeedbackVerdict = 'agree' | 'disagree' | 'unsure';

export type StaleAnalysisNotice = {
  displayName: string;
  runId: string;
  changedDocNames: string[];
  inputsInferred: boolean;
};

export type ChatErrorData = {
  code: string;
  retryable: boolean;
  message: string;
};

export type ChatArtifact = {
  savedArtifactId?: string;
  revision?: number;
  artifactId: string;
  attachmentId?: string | null;
  name: string;
  url: string;
  sizeBytes?: number;
};

export type SpecialistResult = {
  resultId: string;
  answer: string;
  citations?: Citation[];
  artifacts: ChatArtifact[];
};

export type OperationState =
  | 'dispatching'
  | 'running'
  | 'retrying'
  | 'unknown'
  | 'succeeded'
  | 'failed'
  | 'aborted';

export type OperationRun = {
  activityOmitted?: number;
  id: string;
  messageId: string;
  displayName: string;
  state: OperationState;
  runIds: string[];
  startedAt?: string | null;
  completedAt?: string | null;
  steps: Array<{ id: string; displayName: string; state: OperationState; attempt: number }>;
};

export type Message = {
  lastEventId?: number;
  openedArtifactId?: string | null;
  turnStatus?: 'completed' | 'pending' | 'failed' | 'cancelled';
  operationRuns?: OperationRun[];
  id: string;
  message: string;
  role: ChatRole;
  specifiedDocuments?: string[];
  citations: Citation[];
  isLoading?: boolean;
  clarifications?: ClarificationData[];
  clarificationResponse?: GenericClarification;
  navigate?: { entity: string; entityId: string; label: string };
  error?: ChatErrorData;
  insufficientBasis?: InsufficientBasisData;
  runCompletions?: RunCompletedData[];
  revisionDeltas?: RevisionDeltaData[];
  webFindings?: WebFindingsData[];
  artifacts?: ChatArtifact[];
  specialistResults?: SpecialistResult[];
  preview?: DeltaState;
  statusLabel?: string;
  createdAt?: string | null;
  completedAt?: string | null;
  thinkingSteps?: ThinkingStepData[];
  feedback?: ChatFeedbackVerdict | null;
};

export type ChatContinuation = {
  id: string;
  state:
    | 'registering'
    | 'waiting'
    | 'running'
    | 'completed'
    | 'cancelled'
    | 'failed'
    | 'suppressed';
};

export type ChatEmailNotification = {
  status: 'on' | 'off' | 'not_running';
};

export type ChatThread = {
  pendingCreation?: boolean;
  continuations?: ChatContinuation[];
  backgroundRunning?: boolean;
  messages: Message[];
  pendingTail: { messageId: string; afterEventId: number } | null;
  staleAnalyses: StaleAnalysisNotice[] | null;
  emailNotification?: ChatEmailNotification | null;
};

type ClarificationAnswer = {
  clarificationId: string;
  actionType: string;
  selected: string[];
  freeformInput: string | null;
};

export type GenericClarification = ClarificationAnswer | ClarificationAnswer[];

export type ChatHistoryEntry = {
  id: string;
  eval_id: string;
  eval_type: DocumentType;
  title: string | null;
  created_at: string;
  last_activity_at?: string | null;
  is_running?: boolean;
  pinned_at: string | null;
  created_by?: string | null;
  created_by_name?: string | null;
  shared?: boolean;
};

export type ChatHydrateEvent = {
  eventId: number;
  eventType: ChatEventType;
  payload: any;
};

export type ChatSurface =
  | 'project'
  | 'tender'
  | 'contractual_supplement'
  | 'contract'
  | 'bid'
  | 'bid_comparison'
  | 'kg_work_package'
  | 'legal_deviation'
  | 'custom_topic'
  | 'conflict_finder'
  | 'clause_scanner'
  | 'ambiguity'
  | 'standard_deviation'
  | 'summary'
  | 'bid_compliance'
  | 'bid_draft'
  | 'bid_methodology';

export type ChatQueryParams = Record<string, string | number | boolean | undefined>;

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    chatSurface?: ChatSurface;
  }
}

export type SourceStreamParams = {
  openedArtifactId?: string | null;
  evalId: string;
  evalType: DocumentType;
  userPrompt: string;
  threadId: string;
  specifiedDocuments?: string[];
  clarificationResponse?: GenericClarification;
  analysisParams?: Record<string, unknown>;
};

export type ChatHydrateMessage = {
  lastEventId?: number;
  openedArtifactId?: string | null;
  origin?: 'continuation' | null;
  continuationId?: string | null;
  id: string;
  userMessage: string;
  assistantMessage: string;
  status: 'completed' | 'pending' | 'failed' | 'cancelled';
  createdAt: string;
  completedAt?: string | null;
  feedback: ChatFeedbackVerdict | null;
  events: ChatHydrateEvent[];
};
